import type { AxisValueFormatterContext } from "@mui/x-charts/models";
import dayjs from "dayjs";

export const CONTACTS_RANGE_PRESETS = [
  "7",
  "30",
  "3m",
  "6m",
  "9m",
  "1y",
] as const;
export type ContactsRangePreset = (typeof CONTACTS_RANGE_PRESETS)[number];
export type ContactsChartMode = "daily" | "monthly";

const MONTHS_BY_PRESET = { "3m": 3, "6m": 6, "9m": 9, "1y": 12 } as const;
const DENSE_DAILY_THRESHOLD = 30;

export const shouldShowContactsMark = (
  counts: ReadonlyArray<number | null>,
  index: number,
  mode: ContactsChartMode,
) =>
  mode === "monthly" ||
  counts.length <= DENSE_DAILY_THRESHOLD ||
  // A point surrounded by missing days has no visible line segment.
  (counts[index - 1] == null && counts[index + 1] == null);

export const getContactsTickInterval = (
  pointCount: number,
  mode: ContactsChartMode,
) => {
  if (mode === "monthly" || pointCount <= DENSE_DAILY_THRESHOLD) return "auto";
  const interval = Math.ceil(pointCount / 8);
  return (_value: unknown, index: number) => index % interval === 0;
};

export const formatContactsChartDate = (
  date: string,
  mode: ContactsChartMode,
  location: AxisValueFormatterContext["location"],
  longRange: boolean,
) => {
  if (mode === "monthly") return dayjs(date).format("YYYY/MM");
  if (location === "tooltip") return dayjs(date).format("YYYY/MM/DD");
  return dayjs(date).format(longRange ? "YY/M/D" : "M/D");
};

export const supportsMonthlyAverage = (preset: ContactsRangePreset) =>
  preset !== "7" && preset !== "30";

export const getContactsDateRange = (
  preset: ContactsRangePreset,
  today: string,
) => {
  const reference = dayjs(today);
  // Today's snapshot only includes activity through yesterday.
  const end = reference.subtract(1, "day");
  const start =
    preset === "7" || preset === "30"
      ? end.subtract(Number(preset) - 1, "day")
      : reference.subtract(MONTHS_BY_PRESET[preset], "month").startOf("month");

  return {
    reportingFrom: start.format("YYYY-MM-DD"),
    reportingTo: end.format("YYYY-MM-DD"),
    // The API accepts snapshot bounds; each snapshot describes the previous day.
    dateFrom: start.add(1, "day").format("YYYY-MM-DD"),
    dateTo: end.add(1, "day").format("YYYY-MM-DD"),
  };
};

export const buildContactsChartData = (
  records: ReadonlyArray<{ date: string; count: number }>,
  reportingFrom: string,
  reportingTo: string,
  mode: ContactsChartMode,
  dataAvailableFrom: string,
) => {
  const buckets = new Map<string, { total: number; days: number }>();
  for (const record of records) {
    if (
      record.date < reportingFrom ||
      record.date > reportingTo ||
      record.date < dataAvailableFrom
    )
      continue;
    const key =
      mode === "monthly" ? `${record.date.slice(0, 7)}-01` : record.date;
    const bucket = buckets.get(key) ?? { total: 0, days: 0 };
    bucket.total += record.count;
    bucket.days += 1;
    buckets.set(key, bucket);
  }

  const dates: Array<string> = [];
  const counts: Array<number | null> = [];
  const unit = mode === "monthly" ? "month" : "day";
  let cursor = dayjs(reportingFrom).startOf(unit);
  while (cursor.format("YYYY-MM-DD") <= reportingTo) {
    const date = cursor.format("YYYY-MM-DD");
    const bucket = buckets.get(date);
    dates.push(date);
    // Missing days are not zeroes and must not dilute the monthly average.
    counts.push(bucket ? bucket.total / bucket.days : null);
    cursor = cursor.add(1, unit);
  }
  return { dates, counts };
};
